import { useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { Accordion } from '@mantine/core';

import { Routes } from '~/domains/common/constants/routes';
import { useExpirationCountdown } from '~/domains/common/hooks/useExpirationCountdown';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import dt from '~/testing';

import type { MissionAssignment } from '../../types/missions.types';
import { MissionBadge } from '../MissionBadge/MissionBadge';

import { useStyles } from './MissionStartedModal.styles';

export interface MissionStartedModalProps {
  isOpen: boolean;
  isClosing: boolean;
  onClose: () => void;
  missionAssignment?: MissionAssignment;
}

function MissionStartedModal({
  isOpen,
  isClosing,
  onClose,
  missionAssignment,
}: MissionStartedModalProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('promotions');
  const router = useRouter();

  const mission = missionAssignment?.mission;
  const progress = missionAssignment?.progress;

  // Stable fallback Date so useExpirationCountdown's effect (keyed on targetDate
  // identity) doesn't loop when the mission is evergreen (endsAt === null).
  const fallbackDateRef = useRef<Date>();
  if (!fallbackDateRef.current) fallbackDateRef.current = new Date();
  const endsAtDate = useMemo(
    () => (mission?.endsAt ? new Date(mission.endsAt) : null),
    [mission?.endsAt],
  );
  const { value: countdown } = useExpirationCountdown(endsAtDate ?? fallbackDateRef.current);

  const progressWidth = useMemo(() => {
    if (!progress) return '0%';
    if (progress.current === 0) return '10px';
    return `${(progress.current / progress.required) * 100}%`;
  }, [progress]);

  const handleAction = () => {
    // Hardcoded for V1
    if (mission?.type === 'QUICKPICKS') {
      router.push(Routes.quickPicksBoard());
    }
    onClose();
  };

  return (
    <Dialog
      type={{ desktop: 'modal', mobile: 'drawer' }}
      isOpen={isOpen}
      isClosing={isClosing}
      onClose={onClose}
      dataTestId={dt.loyalty.missions.startedModal.container}
      className={classes.dialogContainer}
    >
      <DialogBody className={classes.dialogBody}>
        <div className={classes.dialogGradient} />
          <div className={classes.container}>
            <div className={classes.badge}>
              <MissionBadge size="large" rewardValue={mission.rewardValue} iconUrl={mission.iconUrl} />
            </div>

            <Text variant="title-medium" className={classes.description}>
              {mission.description}
            </Text>

            <div className={classes.progressSection}>
              <div className={classes.progressLabelRow}>
                <Text variant="subhead-small" className={classes.progressLabel}>
                  {mission.title}
                </Text>
                {progress && (
                  <Text variant="subhead-small" className={classes.progressCount}>
                    {progress.current}/{progress.required}
                  </Text>
                )}
              </div>
              <div className={classes.progressTrack}>
                <div className={classes.progressBarFill} style={{ width: progressWidth }} />
              </div>
            </div>

            {endsAtDate && (
              <Text variant="title-small" className={classes.endsIn}>
                {t('missionDetail.endsIn', { countdown })}
              </Text>
            )}

            <Button
              variant="primary-fill"
              size="large"
              fullWidth
              className={classes.cta}
              onClick={handleAction}
              data-test-id={dt.loyalty.missions.startedModal.cta}
            >
              {t('missionDetail.cta.quickpicks')}
            </Button>

            {mission.terms && (
              <div className={classes.terms}>
                <Accordion className={classes.accordion}>
                  <Accordion.Item value="terms" className={classes.accordionItem}>
                    <Accordion.Control className={classes.accordionControl}>
                      <Text variant="subhead-small" style={{ fontWeight: 800 }}>
                          {t('missionDetail.viewTerms')}
                      </Text>
                    </Accordion.Control>
                    <Accordion.Panel className={classes.accordionPanel}>
                      <Text
                        variant="body-medium"
                        className={classes.termsContent}
                        dangerouslySetInnerHTML={{ __html: toSafeMarkup(mission.terms, { useMarkdown: true }) }}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </div>
            )}
          </div>
      </DialogBody>
    </Dialog>
  );
}

export default MissionStartedModal;
